import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popover from '@mui/material/Popover';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Globe, ChevronDown } from 'lucide-react';

// Redesigned timezone selector — mirrors the prototype's TimezoneButton
// (date-picker.jsx): Globe + value + chevron trigger, with the same two-line
// (heading + timezone) options panel used in the Schedule Email modal.
// Options must come from the caller (user/workspace data) — never hardcoded.
const panelPaperSx = {
  marginTop: '4px',
  width: 288,
  maxHeight: 320,
  border: '1px solid #e0e1e6',
  borderRadius: '8px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  padding: '4px',
};

const triggerBase = 'inline-flex items-center gap-2 h-10 px-3 border rounded-[8px] '
  + 'text-sm bg-white text-ink-900 transition ';

function TimezoneDropdown({ value, onChange, options }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <div>
      <button
        type="button"
        aria-label="Timezone"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className={triggerBase + (open ? 'border-brand-500' : 'border-rule hover:border-brand-500')}
      >
        <Globe size={14} className="text-brand-500 flex-shrink-0" />
        <span className="text-sm font-medium whitespace-nowrap">{value}</span>
        <ChevronDown
          size={12}
          className={`text-ink-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: panelPaperSx }}
      >
        {options.map(({ heading, description, value: optionValue }) => {
          const isSelected = optionValue === value;
          return (
            <button
              key={`${heading}-${optionValue}`}
              type="button"
              onClick={() => {
                onChange(optionValue);
                setAnchorEl(null);
              }}
              className={`w-full text-left px-3 py-2 rounded-[6px] transition block ${
                isSelected ? 'bg-[#e8eef7]' : 'hover:bg-surface-page'
              }`}
            >
              <span className={`block text-xs font-semibold mb-0.5 ${
                isSelected ? 'text-brand-700' : 'text-ink-900'
              }`}
              >
                {heading}
              </span>
              <span className={`block text-sm ${isSelected ? 'text-brand-700' : 'text-ink-500'}`}>
                {description}
              </span>
            </button>
          );
        })}
      </Popover>
    </div>
  );
}

TimezoneDropdown.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    heading: PropTypes.string,
    description: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
};

export default TimezoneDropdown;
